import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
});

function GenomicSelection({ crop }) {
  const [activeStep, setActiveStep] = useState(1);
  const [genotypeTables, setGenotypeTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [phenotypeFile, setPhenotypeFile] = useState(null);
  const [phenotypeData, setPhenotypeData] = useState(null);
  const [traits, setTraits] = useState([]);
  const [selectedTrait, setSelectedTrait] = useState('');
  
  // Model configuration
  const [modelType, setModelType] = useState('rrblup');
  const [cvFolds, setCvFolds] = useState(5);
  const [radFilter, setRadFilter] = useState(null);  // '2', '6', or null
  
  // Training results
  const [trainingData, setTrainingData] = useState(null);
  const [trainingResults, setTrainingResults] = useState(null);
  const [gebvResults, setGebvResults] = useState(null);
  const [markerEffects, setMarkerEffects] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchGenotypeTables();
  }, [crop]);

  const fetchGenotypeTables = async () => {
    try {
      let url = '/api/genomic-selection/tables';
      if (crop) url += `?crop=${encodeURIComponent(crop)}`;
      const response = await api.get(url);
      setGenotypeTables(response.data.tables);
    } catch (err) {
      setError('Failed to fetch genotype tables');
    }
  };

  const handlePhenotypeUpload = async () => {
    if (!phenotypeFile) {
      setError('Please select a phenotype file');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', phenotypeFile);

      const response = await api.post(
        '/api/genomic-selection/upload-phenotype',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setPhenotypeData(response.data.phenotype_data);
      setTraits(response.data.traits);
      if (response.data.traits.length > 0) {
        setSelectedTrait(response.data.traits[0]);
      }
      setActiveStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload phenotype data');
    } finally {
      setLoading(false);
    }
  };

  const handlePrepareTrainingData = async () => {
    if (!selectedTable || !selectedTrait || !phenotypeData) {
      setError('Please complete all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(
        '/api/genomic-selection/prepare-training-data',
        {
          genotype_table: selectedTable,
          phenotype_data: phenotypeData,
          trait_name: selectedTrait,
          cv_folds: cvFolds,
          rad_filter: radFilter,
          skip_qc: selectedTable.includes('filtered')
        }
      );

      setTrainingData(response.data);
      setActiveStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to prepare training data');
    } finally {
      setLoading(false);
    }
  };

  const handleTrainModel = async () => {
    if (!selectedTable || !selectedTrait) {
      setError('Missing required configuration');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(
        '/api/genomic-selection/train-model',
        {
          genotype_table: selectedTable,
          trait_name: selectedTrait,
          model_type: modelType,
          cv_folds: cvFolds,
          test_size: 0.2
        }
      );

      setTrainingResults(response.data);
      setActiveStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to train model');
    } finally {
      setLoading(false);
    }
  };

  const handlePredictGEBV = async () => {
    if (!trainingResults || !selectedTable) {
      setError('Model must be trained first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(
        '/api/genomic-selection/predict-gebv',
        {
          trained_model_id: trainingResults.model_id,
          genotype_table: selectedTable,
          sample_ids: null  // Predict all
        }
      );

      setGebvResults(response.data);
      setActiveStep(5);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to predict GEBVs');
    } finally {
      setLoading(false);
    }
  };

  const handleGetMarkerEffects = async () => {
    if (!trainingResults) {
      setError('Model must be trained first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.get(
        `/api/genomic-selection/marker-effects/${trainingResults.model_id}`
      );

      setMarkerEffects(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch marker effects');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => {
    const steps = [
      { num: 1, label: 'Setup' },
      { num: 2, label: 'Training Pop.' },
      { num: 3, label: 'Train Model' },
      { num: 4, label: 'Predict GEBV' },
      { num: 5, label: 'Marker Effects' }
    ];

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
        {steps.map((step, idx) => (
          <div key={step.num} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: activeStep >= step.num ? '#025c4f' : '#ccc',
              color: 'white',
              lineHeight: '40px',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              {step.num}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>{step.label}</div>
            {idx < steps.length - 1 && (
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '50%',
                width: '100%',
                height: '2px',
                backgroundColor: activeStep > step.num ? '#025c4f' : '#ccc',
                zIndex: -1
              }} />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStep1Setup = () => (
    <div style={{ backgroundColor: '#dbf8f4', padding: '20px', borderRadius: '8px' }}>
      <h3 style={{ color: '#025c4f', marginTop: 0 }}>Step 1: Setup Training Population</h3>
      
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
          Select Genotype Table:
        </label>
        <select
          value={selectedTable}
          onChange={(e) => setSelectedTable(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '4px',
            border: '1px solid #025c4f'
          }}
        >
          <option value="">-- Select Table --</option>
          {genotypeTables.map(table => (
            <option key={table} value={table}>{table}</option>
          ))}
        </select>
      </div>

      {/* Row Type Filter */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
          🌾 Row Type Filter (Barley):
        </label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={() => setRadFilter(radFilter === '2' ? null : '2')}
            style={{
              padding: '8px 20px', borderRadius: '20px', fontSize: '14px', fontWeight: '600',
              cursor: 'pointer', transition: 'all 0.2s',
              border: radFilter === '2' ? '2px solid #2e86de' : '1px solid #adb5bd',
              backgroundColor: radFilter === '2' ? '#2e86de' : '#fff',
              color: radFilter === '2' ? '#fff' : '#495057',
            }}
          >
            2-Row
          </button>
          <button
            onClick={() => setRadFilter(radFilter === '6' ? null : '6')}
            style={{
              padding: '8px 20px', borderRadius: '20px', fontSize: '14px', fontWeight: '600',
              cursor: 'pointer', transition: 'all 0.2s',
              border: radFilter === '6' ? '2px solid #e67e22' : '1px solid #adb5bd',
              backgroundColor: radFilter === '6' ? '#e67e22' : '#fff',
              color: radFilter === '6' ? '#fff' : '#495057',
            }}
          >
            6-Row
          </button>
          <span style={{ fontSize: '13px', color: '#888', marginLeft: '8px' }}>
            {radFilter ? `Training only on ${radFilter}-row barley` : 'All samples'}
          </span>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
          Upload Phenotype Data (CSV):
        </label>
        <input
          type="file"
          accept=".csv"
          onChange={(e) => setPhenotypeFile(e.target.files[0])}
          style={{ marginBottom: '10px' }}
        />
        <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          Required format: First column "sample_id", remaining columns are traits
        </div>
      </div>

      <button
        onClick={handlePhenotypeUpload}
        disabled={loading || !phenotypeFile || !selectedTable}
        style={{
          backgroundColor: '#025c4f',
          color: 'white',
          padding: '12px 24px',
          border: 'none',
          borderRadius: '4px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '16px'
        }}
      >
        {loading ? 'Uploading...' : 'Upload & Continue'}
      </button>
    </div>
  );

  const renderStep2TrainingPopulation = () => (
    <div style={{ backgroundColor: '#dbf8f4', padding: '20px', borderRadius: '8px' }}>
      <h3 style={{ color: '#025c4f', marginTop: 0 }}>Step 2: Prepare Training Population</h3>
      
      {phenotypeData && (
        <>
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '4px' }}>
            <h4 style={{ marginTop: 0 }}>Phenotype Data Summary</h4>
            <p><strong>Traits Available:</strong> {traits.join(', ')}</p>
            <p><strong>Total Records:</strong> {phenotypeData.length}</p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
              Select Trait for Analysis:
            </label>
            <select
              value={selectedTrait}
              onChange={(e) => setSelectedTrait(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #025c4f'
              }}
            >
              {traits.map(trait => (
                <option key={trait} value={trait}>{trait}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
              Cross-Validation Folds:
            </label>
            <input
              type="number"
              min="3"
              max="10"
              value={cvFolds}
              onChange={(e) => setCvFolds(parseInt(e.target.value))}
              style={{
                width: '100px',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #025c4f'
              }}
            />
          </div>

          <button
            onClick={handlePrepareTrainingData}
            disabled={loading || !selectedTrait}
            style={{
              backgroundColor: '#025c4f',
              color: 'white',
              padding: '12px 24px',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {loading ? 'Preparing...' : 'Prepare Training Data'}
          </button>
        </>
      )}

      {trainingData && (
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e8f9f6', borderRadius: '4px' }}>
          <h4 style={{ marginTop: 0, color: '#025c4f' }}>✓ Training Data Ready</h4>
          <p><strong>Samples:</strong> {trainingData.n_samples}</p>
          <p><strong>Markers:</strong> {trainingData.n_markers}</p>
          <p><strong>Trait:</strong> {selectedTrait}</p>
          <div style={{ marginTop: '10px' }}>
            <strong>Trait Statistics:</strong>
            <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
              <li>Mean: {trainingData.trait_stats.mean.toFixed(2)}</li>
              <li>Std Dev: {trainingData.trait_stats.std.toFixed(2)}</li>
              <li>Range: {trainingData.trait_stats.min.toFixed(2)} - {trainingData.trait_stats.max.toFixed(2)}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3TrainModel = () => (
    <div style={{ backgroundColor: '#dbf8f4', padding: '20px', borderRadius: '8px' }}>
      <h3 style={{ color: '#025c4f', marginTop: 0 }}>Step 3: Train Prediction Model</h3>
      
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
          Select Model Type:
        </label>
        <select
          value={modelType}
          onChange={(e) => setModelType(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '4px',
            border: '1px solid #025c4f'
          }}
        >
          <optgroup label="Linear / Bayesian Models">
            <option value="rrblup">RR-BLUP (Ridge Regression)</option>
            <option value="gblup">GBLUP (Genomic BLUP)</option>
            <option value="bayesian">Bayesian Ridge</option>
            <option value="lasso">Lasso (BayesA-like)</option>
            <option value="elastic_net">Elastic Net (BayesC-like)</option>
          </optgroup>
          <optgroup label="Machine Learning Models">
            <option value="svr">Support Vector Regression (SVR)</option>
            <option value="rf">Random Forest</option>
            <option value="gradient_boosting">Gradient Boosting</option>
            <option value="adaboost">AdaBoost</option>
            <option value="xgboost">XGBoost</option>
            <option value="lightgbm">LightGBM</option>
          </optgroup>
        </select>
        
        <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          {modelType === 'rrblup' && 'Fast, assumes all markers have equal variance — standard GS baseline'}
          {modelType === 'gblup' && 'Uses genomic relationship matrix, equivalent to RR-BLUP'}
          {modelType === 'bayesian' && 'Allows variable marker effects, good for traits with major QTLs'}
          {modelType === 'lasso' && 'L1 regularization — selects important markers, sets others to zero (BayesA-like)'}
          {modelType === 'elastic_net' && 'Combined L1+L2 regularization — balances marker selection and shrinkage (BayesC-like)'}
          {modelType === 'svr' && 'Kernel-based model — captures non-linear marker effects'}
          {modelType === 'rf' && 'Non-linear ensemble — captures epistatic interactions between markers'}
          {modelType === 'gradient_boosting' && 'Sequential boosting — builds strong predictor from weak learners'}
          {modelType === 'adaboost' && 'Adaptive boosting — focuses on hard-to-predict samples'}
          {modelType === 'xgboost' && 'Advanced gradient boosting — often highest accuracy, handles missing data'}
          {modelType === 'lightgbm' && 'Fast gradient boosting — very efficient for high-dimensional genomic data'}
        </div>
      </div>

      <button
        onClick={handleTrainModel}
        disabled={loading || !trainingData}
        style={{
          backgroundColor: '#025c4f',
          color: 'white',
          padding: '12px 24px',
          border: 'none',
          borderRadius: '4px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '16px'
        }}
      >
        {loading ? 'Training...' : 'Train Model'}
      </button>

      {trainingResults && (
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e8f9f6', borderRadius: '4px' }}>
          <h4 style={{ marginTop: 0, color: '#025c4f' }}>✓ Model Training Complete</h4>
          <p><strong>Model:</strong> {trainingResults.model_name}</p>
          
          <div style={{ marginTop: '15px' }}>
            <strong>Training Metrics:</strong>
            <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
              <li>R² Score: {trainingResults.training_metrics.r2.toFixed(3)}</li>
              <li>RMSE: {trainingResults.training_metrics.rmse.toFixed(3)}</li>
              <li>Correlation: {trainingResults.training_metrics.correlation.toFixed(3)}</li>
            </ul>
          </div>

          <div style={{ marginTop: '15px' }}>
            <strong>Cross-Validation ({trainingResults.cross_validation.cv_folds}-Fold):</strong>
            <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
              <li>Mean R²: {trainingResults.cross_validation.mean_r2.toFixed(3)}</li>
              <li>Std R²: {trainingResults.cross_validation.std_r2.toFixed(3)}</li>
            </ul>
          </div>

          {trainingResults.feature_importance && (
            <div style={{ marginTop: '15px' }}>
              <strong>Top 5 Important Markers:</strong>
              <ul style={{ marginTop: '5px', paddingLeft: '20px', fontSize: '12px' }}>
                {trainingResults.feature_importance.slice(0, 5).map((m, i) => (
                  <li key={i}>{m.marker_id}: {m.importance.toFixed(4)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderStep4PredictGEBV = () => (
    <div style={{ backgroundColor: '#dbf8f4', padding: '20px', borderRadius: '8px' }}>
      <h3 style={{ color: '#025c4f', marginTop: 0 }}>Step 4: Predict Genomic Breeding Values</h3>
      
      {!gebvResults && (
        <button
          onClick={handlePredictGEBV}
          disabled={loading || !trainingResults}
          style={{
            backgroundColor: '#025c4f',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px'
          }}
        >
          {loading ? 'Predicting...' : 'Predict GEBVs for All Candidates'}
        </button>
      )}

      {gebvResults && (
        <>
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '4px' }}>
            <h4 style={{ marginTop: 0 }}>GEBV Summary</h4>
            <p><strong>Total Candidates:</strong> {gebvResults.n_candidates}</p>
            <p><strong>Mean GEBV:</strong> {gebvResults.summary.mean_gebv.toFixed(2)}</p>
            <p><strong>GEBV Range:</strong> {gebvResults.summary.min_gebv.toFixed(2)} - {gebvResults.summary.max_gebv.toFixed(2)}</p>
            <p><strong>Mean Reliability:</strong> {gebvResults.summary.mean_reliability.toFixed(3)}</p>
          </div>

          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff9e6', borderRadius: '4px' }}>
            <h4 style={{ marginTop: 0, color: '#025c4f' }}>🌟 Top 10% Selection Candidates</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#025c4f', color: 'white' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Rank</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Sample ID</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>GEBV</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Reliability</th>
                </tr>
              </thead>
              <tbody>
                {gebvResults.top_candidates.map((candidate) => (
                  <tr key={candidate.rank} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '8px' }}>{candidate.rank}</td>
                    <td style={{ padding: '8px' }}>{candidate.sample_id}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#025c4f' }}>
                      {candidate.gebv.toFixed(2)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {candidate.reliability.toFixed(3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: 'white', borderRadius: '4px', padding: '10px' }}>
            <h4 style={{ marginTop: 0 }}>All GEBV Predictions</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0' }}>
                <tr>
                  <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #025c4f' }}>Rank</th>
                  <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #025c4f' }}>Sample ID</th>
                  <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #025c4f' }}>GEBV</th>
                  <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #025c4f' }}>Reliability</th>
                </tr>
              </thead>
              <tbody>
                {gebvResults.predictions.map((pred) => (
                  <tr key={pred.rank} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px' }}>{pred.rank}</td>
                    <td style={{ padding: '6px' }}>{pred.sample_id}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{pred.gebv.toFixed(2)}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{pred.reliability.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  const renderStep5MarkerEffects = () => (
    <div style={{ backgroundColor: '#dbf8f4', padding: '20px', borderRadius: '8px' }}>
      <h3 style={{ color: '#025c4f', marginTop: 0 }}>Step 5: Marker Effects & QTL Detection</h3>
      
      {!markerEffects && (
        <button
          onClick={handleGetMarkerEffects}
          disabled={loading || !trainingResults}
          style={{
            backgroundColor: '#025c4f',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px'
          }}
        >
          {loading ? 'Calculating...' : 'Get Marker Effects'}
        </button>
      )}

      {markerEffects && (
        <>
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '4px' }}>
            <h4 style={{ marginTop: 0 }}>Marker Effects Summary</h4>
            <p><strong>Total Markers Analyzed:</strong> {markerEffects.n_markers}</p>
            <p><strong>Significant Markers (p &lt; 0.05):</strong> {markerEffects.significant_markers.length}</p>
          </div>

          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff9e6', borderRadius: '4px' }}>
            <h4 style={{ marginTop: 0, color: '#025c4f' }}>🎯 Top Significant Markers</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#025c4f', color: 'white' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Marker ID</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Chromosome</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Position</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Effect</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Importance</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>P-value</th>
                </tr>
              </thead>
              <tbody>
                {markerEffects.significant_markers.map((marker, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '8px' }}>{marker.marker_id}</td>
                    <td style={{ padding: '8px' }}>{marker.chromosome}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{marker.position.toLocaleString()}</td>
                    <td style={{ 
                      padding: '8px', 
                      textAlign: 'right',
                      color: marker.effect > 0 ? '#028a5c' : '#c23b22',
                      fontWeight: 'bold'
                    }}>
                      {marker.effect.toFixed(4)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{marker.importance.toFixed(4)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{marker.p_value.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: 'white', borderRadius: '4px', padding: '10px' }}>
            <h4 style={{ marginTop: 0 }}>All Marker Effects (Top 30)</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f0f0f0' }}>
                <tr>
                  <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #025c4f' }}>Marker</th>
                  <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #025c4f' }}>Chr</th>
                  <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #025c4f' }}>Position</th>
                  <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #025c4f' }}>Effect</th>
                  <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #025c4f' }}>Importance</th>
                </tr>
              </thead>
              <tbody>
                {markerEffects.marker_effects.map((marker, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px' }}>{marker.marker_id}</td>
                    <td style={{ padding: '6px' }}>{marker.chromosome}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{marker.position.toLocaleString()}</td>
                    <td style={{ 
                      padding: '6px', 
                      textAlign: 'right',
                      color: marker.effect > 0 ? '#028a5c' : '#c23b22'
                    }}>
                      {marker.effect.toFixed(4)}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>{marker.importance.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ color: '#025c4f', marginBottom: '10px' }}>🧬 Genomic Selection</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Train prediction models and select superior breeding candidates using genomic data
      </p>

      {renderStepIndicator()}

      {error && (
        <div style={{
          padding: '15px',
          backgroundColor: '#ffebee',
          border: '1px solid #ef5350',
          borderRadius: '4px',
          marginBottom: '20px',
          color: '#c62828'
        }}>
          {error}
        </div>
      )}

      {activeStep === 1 && renderStep1Setup()}
      {activeStep === 2 && renderStep2TrainingPopulation()}
      {activeStep === 3 && renderStep3TrainModel()}
      {activeStep === 4 && renderStep4PredictGEBV()}
      {activeStep === 5 && renderStep5MarkerEffects()}

      {activeStep > 1 && (
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
            style={{
              backgroundColor: '#8bcec0',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Previous Step
          </button>
          {activeStep < 5 && trainingData && (
            <button
              onClick={() => setActiveStep(Math.min(5, activeStep + 1))}
              style={{
                backgroundColor: '#025c4f',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Next Step →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default GenomicSelection;
